import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  IConfigPerfil,
  ICreatePuesta,
  IFilter,
  IGeoJSONPoint,
  IPuesta,
  IQueryParam,
  IUpdatePuesta,
  TipoEntidadConfigPerfil,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { MapaUbicarComponent } from '../../../../auxiliares/mapas/mapa-ubicar/mapa-ubicar.component';
import { GrupoSelectorComponent } from '../../../../componentes/selectores/grupo-selector/grupo-selector.component';
import { CardHeaderComponent } from '../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../componentes/content-card/content-card.component';
import { ButtonComponent } from '../../../../auxiliares/button/button.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { PuestasService } from '../../../../auxiliares/servicios/http/puestas.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { CrearEditarConfigPerfilComponent } from '../../config-perfil/crear-editar-config-perfil/crear-editar-config-perfil.component';
import { LoginService } from '../../../login/login.service';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';

// Tipos de perfil que una puesta puede crear: cualquiera salvo el ACTIS
// Dimming (se referencia desde un perfil General, no se asigna directo).
const TIPOS_PERFIL_PUESTA = [
  'Luminaria GPE',
  'Luminaria Wellness',
  'Luminaria ACTIS FING General',
] as const;

@Component({
  imports: [
    AuxiliaresModule,
    MapaUbicarComponent,
    GrupoSelectorComponent,
    CardHeaderComponent,
    ContentCardComponent,
    ButtonComponent,
    MatDividerModule,
  ],
  selector: 'app-crear-editar-puesta',
  templateUrl: './crear-editar-puesta.component.html',
  styleUrl: './crear-editar-puesta.component.scss',
})
export class CrearEditarPuestaComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private service = inject(PuestasService);
  private lista2 = inject(Lista2Service);
  private formularios = inject(FormularioDialogService);
  dialogRef = inject<MatDialogRef<CrearEditarPuestaComponent>>(MatDialogRef, {
    optional: true,
  });
  data = inject<{ edit?: IPuesta }>(MAT_DIALOG_DATA, { optional: true });

  public loading = false;
  public title = 'Crear Puesta';
  public form?: FormGroup;
  public puesta?: IPuesta;
  public centrarA?: IGeoJSONPoint;

  // Perfiles a nivel puesta (tipoEntidad 'Luminaria')
  public perfilesConfig: IConfigPerfil[] = [];
  private perfilesConfig$?: Subscription;

  get geojson() {
    return this.form?.get('ubicacion') as FormGroup;
  }

  private createForm() {
    this.form = new FormGroup({
      identificacion: new FormControl(
        this.puesta?.identificacion,
        Validators.required,
      ),
      direccion: new FormControl(this.puesta?.direccion),
      ubicacion: new FormGroup({
        type: new FormControl('Point'),
        coordinates: new FormControl(this.puesta?.ubicacion?.coordinates),
      }),
      // Membresía en grupos de piestas (grupos categoría 'Puesta')
      idsGrupos: new FormControl(this.puesta?.idsGrupos),
      // Perfil(es) a nivel puesta: máximo uno por tipo de luminaria.
      idsPerfilConfig: new FormControl(this.puesta?.idsPerfilConfig || [], [
        this.validarPerfilesUnicosPorTipo.bind(this),
      ]),
    });
  }

  // Validador: no más de un perfil del mismo tipoConfig.
  private validarPerfilesUnicosPorTipo(
    control: AbstractControl,
  ): ValidationErrors | null {
    const ids: string[] = control.value || [];
    if (!ids.length || !this.perfilesConfig?.length) return null;
    const tiposVistos = new Set<string>();
    for (const id of ids) {
      const perfil = this.perfilesConfig.find((p) => p._id === id);
      if (!perfil?.tipoConfig) continue;
      if (tiposVistos.has(perfil.tipoConfig)) return { tipoDuplicado: true };
      tiposVistos.add(perfil.tipoConfig);
    }
    return null;
  }

  // True si el perfil debe deshabilitarse: ya hay otro del mismo tipo elegido.
  public perfilDeshabilitado(perfil: IConfigPerfil): boolean {
    const seleccionados: string[] =
      this.form?.get('idsPerfilConfig')?.value || [];
    if (seleccionados.includes(perfil._id!)) return false;
    return seleccionados.some((id) => {
      const otro = this.perfilesConfig?.find((p) => p._id === id);
      return otro?.tipoConfig === perfil.tipoConfig;
    });
  }

  // Crear un perfil inline (cualquier tipo salvo ACTIS Dimming) y agregarlo a
  // la selección. El validador de tipos únicos avisa si quedara duplicado.
  public async crearPerfil(): Promise<void> {
    const ref = this.formularios.abrir(CrearEditarConfigPerfilComponent, {
      tamano: 'mediano',
      data: {
        tipoEntidad: 'Luminaria',
        tiposConfigPermitidos: [...TIPOS_PERFIL_PUESTA],
      },
    });
    const creado: IConfigPerfil | undefined = await firstValueFrom(
      ref.afterClosed(),
    );
    if (creado?._id) {
      this.listarPerfilesConfig();
      const actuales: string[] = this.form?.get('idsPerfilConfig')?.value || [];
      this.form?.patchValue({ idsPerfilConfig: [...actuales, creado._id] });
    }
  }

  private listarPerfilesConfig(): void {
    const tipoEntidad: TipoEntidadConfigPerfil = 'Luminaria';
    // Solo perfiles del cliente de la puesta (o del propio al crear) + globales.
    // Antes traía perfiles de todo el árbol accesible.
    const idCliente =
      this.puesta?.idCliente ?? LoginService.getClientePropio()?._id;
    const filter: IFilter<IConfigPerfil> = idCliente
      ? { tipoEntidad, $or: [{ idCliente }, { global: true }] }
      : { tipoEntidad };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      includeChildren: !idCliente,
      sort: '-fechaCreacion',
    };
    this.perfilesConfig$?.unsubscribe();
    this.perfilesConfig$ = this.lista2
      .configPerfils(query, 30)
      .subscribe((data) => {
        // Los "ACTIS FING Dimming" se referencian desde un General, no se asignan directo.
        this.perfilesConfig = (data.datos || []).filter(
          (pc) => pc?._id && pc.tipoConfig !== 'Luminaria ACTIS FING Dimming',
        );
      });
  }

  public patchDireccion(direccion: string) {
    this.form?.patchValue({ direccion });
  }

  public async enviar(): Promise<void> {
    if (!this.form?.valid) return;
    this.loading = true;
    try {
      const dato = this.form.value as ICreatePuesta & IUpdatePuesta;
      // Sin coordenadas (nadie ubicó el marcador), no mandar `ubicacion`: un
      // `{ type: 'Point' }` sin coordenadas no es un GeoJSON válido.
      if (dato.ubicacion?.coordinates?.length !== 2) delete dato.ubicacion;
      let resultado: IPuesta;
      if (this.puesta?._id) {
        resultado = await this.service.update(this.puesta._id, dato);
        this.helper.notifSuccess('Puesta actualizada');
      } else {
        resultado = await this.service.create(dato);
        this.helper.notifSuccess('Puesta creada');
      }
      this.dialogRef?.close(resultado);
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  public close(): void {
    this.dialogRef?.close();
  }

  ngOnInit(): void {
    this.puesta = this.data?.edit;
    this.title = this.puesta ? 'Editar Puesta' : 'Crear Puesta';
    if (this.puesta?.ubicacion) this.centrarA = this.puesta.ubicacion;
    this.createForm();
    this.listarPerfilesConfig();
  }

  ngOnDestroy(): void {
    this.perfilesConfig$?.unsubscribe();
  }
}
